
import XcoinsPointsHero from "@/components/xcoins/PointsHero.vue";
import {
  formatPoints,
  getCoverThumbUrl,
  getProductDescription,
  getProductPoints,
  getProductTitle,
  isPointsApiSuccess,
  isProductOutOfStock,
  normalizeMallGoodsList,
} from "@/utils/mallGoods";
import {
  applyManualMallSite,
  applyMallSiteState,
  ensureMallRegions,
  refreshMallRegions,
  ensureMallSite,
  findCountryInList,
  getMallCountry,
  readStoredMallSite,
  resolveMallSite,
} from "@/utils/mallSite";

const DEFAULT_PAGE_SIZE = 20;

function getFirstPageMallLimit(lotteryCount, pageSize = DEFAULT_PAGE_SIZE) {
  if (lotteryCount >= pageSize) {
    return 0;
  }
  return pageSize - lotteryCount;
}

function getMallFetchParams(logicalPage, lotteryCount, pageSize = DEFAULT_PAGE_SIZE) {
  const firstPageMallLimit = getFirstPageMallLimit(lotteryCount, pageSize);

  if (logicalPage <= 1) {
    return {
      page: 1,
      limit: firstPageMallLimit,
      sliceStart: 0,
      sliceEnd: firstPageMallLimit,
      needNextPage: false,
      goodsType: 0,
    };
  }

  // 第一页被抽奖占位后，后续逻辑页在接口页内有偏移；limit 必须固定为 pageSize，
  // 否则 (page-1)*limit 会跳过错误区间，最后一页返回条数少于 sliceStart 会被切空。
  const mallStartIndex = firstPageMallLimit + (logicalPage - 2) * pageSize;
  const apiPage = Math.floor(mallStartIndex / pageSize) + 1;
  const offsetInPage = mallStartIndex % pageSize;

  return {
    page: apiPage,
    limit: pageSize,
    sliceStart: offsetInPage,
    sliceEnd: offsetInPage + pageSize,
    needNextPage: offsetInPage > 0,
    goodsType: 0,
  };
}

function calcPaginationPageCount(lotteryCount, mallTotal, pageSize = DEFAULT_PAGE_SIZE) {
  const firstPageMallLimit = getFirstPageMallLimit(lotteryCount, pageSize);
  const firstPageMallCount = Math.min(firstPageMallLimit, mallTotal);
  const remainingMall = Math.max(0, mallTotal - firstPageMallCount);

  if (lotteryCount === 0 && mallTotal === 0) {
    return 0;
  }
  if (remainingMall <= 0) {
    return 1;
  }
  return 1 + Math.ceil(remainingMall / pageSize);
}

function sliceMallList(list, sliceStart, sliceEnd) {
  if (sliceEnd <= sliceStart) {
    return [];
  }
  return list.slice(sliceStart, sliceEnd);
}

async function fetchMallTotal($global) {
  const mallRes = await $global.pointsMallList({ page: 1, limit: 1, goodsType: 0 }).catch(() => null);
  if (isPointsApiSuccess(mallRes)) {
    const data = mallRes.data || {};
    return Number(data.total) || 0;
  }
  return 0;
}

/** 按固定 pageSize 拉一页；偏移时再拼下一页，避免最后一页切片越界 */
async function fetchMallSlice($global, mallParams) {
  if (mallParams.limit <= 0) {
    return {
      list: [],
      total: await fetchMallTotal($global),
      error: false,
    };
  }

  const firstRes = await $global.pointsMallList({
    page: mallParams.page,
    limit: mallParams.limit,
    goodsType: mallParams.goodsType,
  }).catch(() => null);

  if (!isPointsApiSuccess(firstRes)) {
    return { list: [], total: 0, error: true };
  }

  const firstData = firstRes.data || {};
  let list = normalizeMallGoodsList(Array.isArray(firstData.list) ? firstData.list : []);
  const total = Number(firstData.total) || 0;

  if (mallParams.needNextPage) {
    const nextRes = await $global.pointsMallList({
      page: mallParams.page + 1,
      limit: mallParams.limit,
      goodsType: mallParams.goodsType,
    }).catch(() => null);
    if (isPointsApiSuccess(nextRes)) {
      const nextData = nextRes.data || {};
      list = list.concat(
        normalizeMallGoodsList(Array.isArray(nextData.list) ? nextData.list : [])
      );
    }
  }

  return {
    list: sliceMallList(list, mallParams.sliceStart, mallParams.sliceEnd),
    total,
    error: false,
  };
}

function normalizeLotteryActivityList(list) {
  return list.filter(Boolean);
}

async function fetchExchangeLists($global, { page, limit = DEFAULT_PAGE_SIZE }) {
  const lotteryRes = await $global.pointsLotteryActivityList().catch(() => null);
  const lotteryActivityList = isPointsApiSuccess(lotteryRes)
    ? normalizeLotteryActivityList(Array.isArray(lotteryRes.data) ? lotteryRes.data : [])
    : [];

  const mallParams = getMallFetchParams(page, lotteryActivityList.length, limit);
  const mallSlice = await fetchMallSlice($global, mallParams);
  const mallList = mallSlice.error ? [] : mallSlice.list;
  const mallTotal = mallSlice.total;

  return {
    lotteryActivityList,
    mallList,
    mallTotal,
    currentPage: page,
    pageSize: limit,
  };
}

export default {
  components: { XcoinsPointsHero },
  name: "pointsMall",
  layout: "DefaultLayout",
  head() {
    return {
      title: this.$t("xcoins.XCOINS_SHOP"),
      meta: [
        {
          name: "keywords",
          content: "X-Coins mall",
        },
        {
          name: "description",
          content: "X-Coins mall product list",
        },
      ],
    };
  },
  data() {
    return {
      lotteryActivityList: [],
      mallList: [],
      mallTotal: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      currentPage: parseInt(this.$route.query.page) || 1,
      isMobileMall: false,
      mobileLoading: false,
      mobileFinished: false,
      shouldBootstrapOnMount: false,
      pageDataBootstrapped: false,
      mallListFetchKey: "",
      lotteryListFetched: false,
      pendingListRequestKey: "",
      listFetchPromise: null,
      countryOptions: [],
      selectedStation: "",
      selectedCountry: "",
      mallSiteReady: false,
      mallLoadError: false,
    };
  },
  async asyncData({ $global, query }) {
    const page = parseInt(query.page) || 1;
    const limit = DEFAULT_PAGE_SIZE;

    if (process.client) {
      return {
        lotteryActivityList: [],
        mallList: [],
        mallTotal: 0,
        currentPage: page,
        pageSize: limit,
        shouldBootstrapOnMount: true,
      };
    }

    try {
      const data = await fetchExchangeLists($global, { page, limit });
      return {
        ...data,
        shouldBootstrapOnMount: false,
        lotteryListFetched: true,
      };
    } catch (e) {}

    return {
      lotteryActivityList: [],
      mallList: [],
      mallTotal: 0,
      currentPage: page,
      pageSize: limit,
      shouldBootstrapOnMount: false,
      lotteryListFetched: true,
    };
  },
  computed: {
    displayLotteryList() {
      if (this.isMobileMall) {
        return this.lotteryActivityList;
      }
      return this.currentPage === 1 ? this.lotteryActivityList : [];
    },
    paginationPageCount() {
      return calcPaginationPageCount(
        this.lotteryActivityList.length,
        this.mallTotal,
        this.pageSize || DEFAULT_PAGE_SIZE
      );
    },
  },
  watch: {
    "$route.query.page"(newVal, oldVal) {
      if (!process.client || newVal === oldVal) {
        return;
      }

      const page = parseInt(newVal) || 1;
      this.fetchExchangePageData({
        page,
        includeLottery: page === 1,
        force: true,
      });
    },
    "$i18n.locale"(newVal, oldVal) {
      if (!process.client || !newVal || newVal === oldVal) {
        return;
      }
      this.reloadCountryOptions();
    },
  },
  async mounted() {
    await this.initMallSite();

    if (this.shouldBootstrapOnMount || this.selectedCountry) {
      this.pageDataBootstrapped = false;
      this.lotteryListFetched = false;
      this.mallListFetchKey = "";
      this.bootstrapPageData();
    } else {
      this.pageDataBootstrapped = true;
      this.mallListFetchKey = this.getMallListFetchKey(this.currentPage);
      this.lotteryListFetched = true;
    }

    this.updateMallViewport();
    window.addEventListener("resize", this.updateMallViewport);
    window.addEventListener("scroll", this.handleMobileScroll, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateMallViewport);
    window.removeEventListener("scroll", this.handleMobileScroll);
  },
  methods: {
    handleCurrentChange(val) {
      if (this.isMobileMall) {
        return;
      }

      this.$router.push({
        path: "/xcoins/exchange",
        query: { page: val },
      });
    },
    updateMallViewport() {
      const nextIsMobile = window.innerWidth <= 768;
      const wasMobile = this.isMobileMall;
      this.isMobileMall = nextIsMobile;

      if (!nextIsMobile) {
        this.mobileLoading = false;
        return;
      }

      this.mobileFinished = this.currentPage >= this.paginationPageCount;
      if (!wasMobile && this.currentPage !== 1) {
        this.currentPage = 1;
        this.$router.replace({ path: "/xcoins/exchange" });
        this.fetchExchangePageData({ page: 1, includeLottery: true, force: true });
      }
    },
    handleMobileScroll() {
      if (!this.isMobileMall || this.mobileLoading || this.mobileFinished) {
        return;
      }

      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      const listContent = this.$refs.mallListContent;

      if (!listContent) {
        return;
      }

      if (listContent.getBoundingClientRect().bottom <= viewportHeight + 160) {
        this.loadMoreMallList();
      }
    },
    async loadMoreMallList() {
      if (this.mobileLoading || this.mobileFinished) {
        return;
      }

      await this.fetchMallList(this.currentPage + 1, true);
    },
    getMallListFetchKey(page = this.currentPage) {
      return `${page}-${this.lotteryActivityList.length}-${this.pageSize || DEFAULT_PAGE_SIZE}-${getMallCountry()}`;
    },
    applyMallSite({ country, station, source, persist = false }) {
      const resolved = persist
        ? applyManualMallSite({ country, station }, this.$cookies)
        : applyMallSiteState({ country, station, source });
      this.selectedCountry = resolved.country;
      this.selectedStation = resolved.station;
    },
    /** 页脚切语种后重拉国家下拉，已选国家 code 保持不变 */
    async reloadCountryOptions() {
      const { countries } = await refreshMallRegions(this.$global);
      this.countryOptions = countries;
      const current = findCountryInList(countries, this.selectedCountry);
      if (current) {
        this.selectedCountry = current.code;
      }
    },
    /** 下拉只渲染接口 countries，不按 stationCode 合并 */
    async initMallSite() {
      await ensureMallSite(this.$cookies);
      const { countries, stations } = await ensureMallRegions(this.$global);
      this.countryOptions = countries;

      const resolved = resolveMallSite({
        countries,
        stations,
        storedSite: readStoredMallSite(this.$cookies),
        ipCountry: getMallCountry(),
      });
      this.applyMallSite({
        ...resolved,
        persist: false,
      });
      this.mallSiteReady = true;
    },
    /**
     * 选中国家后同时写入 country ISO 与对应 stationCode，再刷新商品列表。
     */
    handleCountryChange(countryCode) {
      const country = findCountryInList(this.countryOptions, countryCode);
      this.applyMallSite({
        country: country ? country.code : countryCode,
        station: country ? country.stationCode : "",
        source: "manual",
        persist: true,
      });

      this.currentPage = 1;
      this.mallListFetchKey = "";
      this.mobileFinished = false;
      if (this.$route.query.page) {
        this.$router.replace({ path: "/xcoins/exchange" });
        return;
      }
      this.fetchExchangePageData({
        page: 1,
        includeLottery: true,
        force: true,
      });
    },
    bootstrapPageData() {
      if (this.pageDataBootstrapped) {
        return;
      }

      this.pageDataBootstrapped = true;
      return this.fetchExchangePageData({ page: this.currentPage });
    },
    async fetchExchangePageData({
      page,
      limit,
      append = false,
      includeLottery,
      force = false,
    } = {}) {
      const targetPage = page ?? (parseInt(this.$route.query.page) || 1);
      const targetLimit = limit ?? this.pageSize ?? DEFAULT_PAGE_SIZE;
      const shouldIncludeLottery = includeLottery ?? targetPage === 1;
      const fetchKey = this.getMallListFetchKey(targetPage);
      const shouldFetchMall = force || append || this.mallListFetchKey !== fetchKey;
      const shouldFetchLottery = shouldIncludeLottery && (force || !this.lotteryListFetched);

      if (!shouldFetchMall && !shouldFetchLottery) {
        return;
      }

      const requestKey = `${fetchKey}-${shouldFetchLottery ? "lottery" : ""}-${append ? "append" : ""}`;
      if (this.pendingListRequestKey === requestKey && this.listFetchPromise) {
        return this.listFetchPromise;
      }

      this.pendingListRequestKey = requestKey;
      this.mobileLoading = true;

      this.listFetchPromise = (async () => {
        try {
          const lotteryPromise = shouldFetchLottery
            ? this.$global.pointsLotteryActivityList().catch(() => null)
            : Promise.resolve(null);
          const lotteryRes = await lotteryPromise;

          if (shouldFetchLottery) {
            if (lotteryRes && isPointsApiSuccess(lotteryRes)) {
              this.lotteryActivityList = normalizeLotteryActivityList(
                Array.isArray(lotteryRes.data) ? lotteryRes.data : []
              );
            }
            this.lotteryListFetched = true;
          }

          if (!shouldFetchMall) {
            return;
          }

          const mallParams = getMallFetchParams(
            targetPage,
            this.lotteryActivityList.length,
            targetLimit
          );
          const mallSlice = await fetchMallSlice(this.$global, mallParams);

          if (!mallSlice.error) {
            this.mallList = append ? this.mallList.concat(mallSlice.list) : mallSlice.list;
            this.mallTotal = mallSlice.total;
            this.currentPage = targetPage;
            this.pageSize = targetLimit;
            this.mallLoadError = false;
            this.mobileFinished = this.currentPage >= this.paginationPageCount;
            if (!append) {
              this.mallListFetchKey = fetchKey;
            }
            return;
          }

          this.mallLoadError = true;
          if (!append) {
            this.mallList = [];
            this.$message.error(this.$t("page.NETWORK_ERROR"));
          } else {
            this.$message.error(this.$t("xcoins.REFRESH_FAILED"));
          }
          this.mobileFinished = true;
        } catch (e) {
          if (!append) {
            this.mobileFinished = true;
          }
        } finally {
          this.mobileLoading = false;
          this.pendingListRequestKey = "";
          this.listFetchPromise = null;
        }
      })();

      return this.listFetchPromise;
    },
    async fetchMallList(page = 1, append = false) {
      await this.fetchExchangePageData({
        page,
        append,
        includeLottery: page === 1 && !append,
        force: append || this.mallListFetchKey !== this.getMallListFetchKey(page),
      });
    },
    /** 列表加载失败时手动重拉当前页 */
    reloadMallList() {
      this.mallLoadError = false;
      this.fetchExchangePageData({
        page: this.currentPage || 1,
        includeLottery: true,
        force: true,
      });
    },
    openLotteryActivity(activity) {
      const activityId = activity && activity.id;
      if (!activityId) {
        this.$router.replace({ path: "/xcoins/exchange" });
        return;
      }

      this.$router.push({
        path: "/xcoins/lottery",
        query: { id: activityId },
      });
    },
    openProductDetail(item) {
      const goodsId = item && item.id;
      if (!goodsId) {
        return;
      }
      this.$router.push(`/xcoins/goods/${goodsId}`);
    },
    getCoverThumbUrl,
    getProductTitle,
    getProductDescription,
    getProductPoints,
    formatPoints,
    isProductOutOfStock,
    /** 列表按钮：售罄显示补货中，未售罄显示兑换 */
    getListButtonText(item) {
      return this.$t(isProductOutOfStock(item) ? "xcoins.RESTOCKING" : "xcoins.REDEEM");
    },
    getLotteryCoverImage(activity) {
      const imageUrl = activity && activity.coverImageUrl;
      if (!imageUrl) {
        return "";
      }
      return imageUrl.indexOf("?") === -1
        ? `${imageUrl}?x-oss-process=image/resize,p_40`
        : `${imageUrl}&x-oss-process=image/resize,p_40`;
    },
    getLotteryTitle(activity) {
      return (activity && activity.name) || this.$t("xcoins.XCOINS_SHOP");
    },
  },
};
